/**
 * Why a notification did not arrive.
 *
 * Six things can silence an alert - the system, the master switch, the kind,
 * quiet time, a muted conversation, Do not disturb - and until now the only
 * way to find out which one was to turn all six off and try again. This
 * checks each in turn and says which is the one biting, with the way to
 * undo it beside the answer.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { cn } from '@/lib/cn';
import { bridge, isDesktop } from '@/lib/bridge';
import { useSession } from '@/stores/session';
import { inQuietTime } from '@/App';
import { setMute } from '@/lib/mutes';
import { Icon } from '@/icons/Icon';
import { Button } from './ui';

type Check = {
  id: string;
  label: string;
  /** true = this one is fine. */
  ok: boolean;
  detail: string;
  fix?: { label: string; run: () => void };
};

export function NotifyDoctor() {
  const settings = useSession((s) => s.settings);
  const saveSettings = useSession((s) => s.saveSettings);
  const me = useSession((s) => s.me);
  const [permission, setPermission] = useState<NotificationPermission | 'app' | 'none'>('none');
  const [, tick] = useState(0);

  /* Read again every few seconds: this screen is what somebody stares at
     while changing a Windows setting in another window. */
  useEffect(() => {
    const read = () => {
      setPermission(bridge()?.notify ? 'app' : typeof Notification === 'undefined' ? 'none' : Notification.permission);
      tick((n) => n + 1);
    };
    read();
    const id = window.setInterval(read, 4000);
    return () => window.clearInterval(id);
  }, []);

  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends, enabled: !!me });

  if (!settings) return null;

  const mutes = Object.entries(settings.mutes ?? {}).filter(([, until]) => until > Date.now());
  const offKinds = Object.entries(settings.notify ?? {}).filter(([, on]) => on === false).map(([k]) => k);
  const quietOn = inQuietTime(settings.quiet);

  const checks: Check[] = [
    {
      id: 'system',
      label: isDesktop() ? 'Windows' : 'Your browser',
      ok: permission === 'app' || permission === 'granted',
      detail: permission === 'app' ? 'The desktop app delivers them itself.'
        : permission === 'granted' ? 'Allowed.'
          : permission === 'denied' ? 'Blocked. Allow notifications for Pugo, and check Windows Settings › System › Notifications.'
            : 'Not allowed yet.',
      fix: permission === 'default'
        ? { label: 'Ask now', run: () => void Notification.requestPermission().then(setPermission) }
        : undefined
    },
    {
      id: 'master',
      label: 'Desktop notifications',
      ok: settings.notifications !== false,
      detail: settings.notifications !== false ? 'On.' : 'Off in Pugo, so nothing will show whatever else is set.',
      fix: settings.notifications === false ? { label: 'Turn on', run: () => void saveSettings({ notifications: true }) } : undefined
    },
    {
      id: 'sounds',
      label: 'Sounds',
      ok: settings.sounds !== false,
      detail: settings.sounds !== false ? 'On at ' + Math.round((settings.soundVolume ?? 0.5) * 100) + '%.' : 'Off, so alerts arrive silently.',
      fix: settings.sounds === false ? { label: 'Turn on', run: () => void saveSettings({ sounds: true }) } : undefined
    },
    {
      id: 'kinds',
      label: 'Notification types',
      ok: offKinds.length === 0,
      detail: offKinds.length ? 'Silenced: ' + offKinds.join(', ') + '.' : 'All kinds are allowed.',
      fix: offKinds.length ? { label: 'Allow all', run: () => void saveSettings({ notifyPreset: 'all' }) } : undefined
    },
    {
      id: 'quiet',
      label: 'Quiet time',
      ok: !quietOn,
      detail: quietOn
        ? 'Running now (' + (settings.quiet?.from ?? '') + '–' + (settings.quiet?.to ?? '') + '), so pop-ups are held.'
          + (settings.favoriteExceptions ? ' Pinned friends still get through.' : '')
        : settings.quiet?.on ? 'Set for ' + settings.quiet.from + '–' + settings.quiet.to + ', not running now.' : 'Off.',
      fix: quietOn ? { label: 'Turn off', run: () => void saveSettings({ quiet: { ...(settings.quiet ?? { from: '22:00', to: '08:00' }), on: false } }) } : undefined
    },
    {
      id: 'dnd',
      label: 'Your status',
      ok: me?.status !== 'dnd',
      detail: me?.status === 'dnd'
        ? 'Do not disturb' + (settings.statusRules?.dnd.exceptDms ? ', though direct messages still come through.' : ' silences alerts.')
        : 'Nothing about your status is silencing anything.'
    },
    {
      id: 'mutes',
      label: 'Muted conversations',
      ok: mutes.length === 0 && !(settings.mutedCrews ?? []).length,
      detail: mutes.length || (settings.mutedCrews ?? []).length
        ? mutes.length + ' conversation' + (mutes.length === 1 ? '' : 's')
          + ((settings.mutedCrews ?? []).length ? ' and ' + settings.mutedCrews!.length + ' crew' : '') + ' quieted.'
        : 'None.',
      fix: mutes.length
        ? { label: 'Unmute all', run: () => { for (const [id] of mutes) void setMute(id, 0); } }
        : undefined
    }
  ];

  const bad = checks.filter((c) => !c.ok);
  const friendCount = (friends.data?.friends ?? []).filter((f) => f.favorite).length;

  return (
    <section className="pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <div className="mb-3 flex items-center gap-2.5">
        <Icon name={bad.length ? 'alert' : 'checkCirc'} size={20} className={bad.length ? 'text-warn' : 'text-good'} />
        <div className="min-w-0 flex-1">
          <h3 className="m-0 text-[16.5px] font-bold text-ink">
            {bad.length ? bad.length === 1 ? 'One thing is holding notifications back' : bad.length + ' things are holding notifications back' : 'Notifications should reach you'}
          </h3>
          <p className="mt-0.5 mb-0 text-[13.5px] text-mute">
            Checked live. Change something elsewhere and this updates by itself.
          </p>
        </div>
      </div>

      <ul className="m-0 flex list-none flex-col p-0">
        {checks.map((c) => (
          <li key={c.id} className="flex items-start gap-3 border-b border-line-soft py-2.5 last:border-b-0">
            <Icon
              name={c.ok ? 'checkCirc' : 'alert'}
              size={17}
              className={cn('mt-0.5 flex-none', c.ok ? 'text-good' : 'text-warn')}
            />
            <div className="min-w-0 flex-1">
              <p className="m-0 text-[14.5px] font-semibold text-ink">{c.label}</p>
              <p className="mt-0.5 mb-0 text-[13px] text-mute">{c.detail}</p>
            </div>
            {c.fix ? <Button size="sm" kind="outline" onClick={c.fix.run}>{c.fix.label}</Button> : null}
          </li>
        ))}
      </ul>

      {settings.favoriteExceptions ? (
        <p className="mt-3 mb-0 text-[12.5px] text-mute">
          {friendCount ? friendCount + ' pinned friend' + (friendCount === 1 ? '' : 's') + ' can reach you during quiet time.'
            : 'Pinned friends can reach you during quiet time - you have not pinned anybody yet.'}
        </p>
      ) : null}
    </section>
  );
}
